import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { CloudRain, Sun, Users, Wrench, AlertTriangle, Camera, FileDown, Trash2, Plus, Mail } from "lucide-react";
import { toast } from "sonner";
import { openBrandedPrintWindow } from "@/lib/documentTemplate";

const WEATHER = ["Clear","Cloudy","Rainy","Stormy","Hot","Cold","Windy"];

export default function ProgressReportTab({ pid, user, backendUrl }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0,10), weather: "Clear", labour_count: 0, work_done: "", issues: "", progress: 0, notes: "" });
  const [selected, setSelected] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const canEdit = ["super_admin","director","architect","interior_designer","site_engineer","hr","purchase_manager"].includes(user?.role);
  const canSendDigest = ["super_admin","director","architect","interior_designer","site_engineer"].includes(user?.role);
  const [sendingDigest, setSendingDigest] = useState(false);

  const sendDigest = async () => {
    if (!window.confirm("Send this week's DPR digest to the project client via email?")) return;
    setSendingDigest(true);
    try {
      const { data } = await api.post(`/projects/${pid}/dpr/send-digest`);
      if (data.status === "success") toast.success(`Digest sent to ${data.recipient} · ${data.reports_count} reports`);
      else if (data.status === "skipped") toast.warning(`Skipped: ${data.reason}`);
      else toast.error(data.message || "Send failed");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to send digest"); }
    finally { setSendingDigest(false); }
  };

  const load = () => api.get(`/projects/${pid}/dpr`).then((r) => setRows(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [pid]);

  const create = async () => {
    if (!form.work_done.trim()) return toast.error("Work done is required");
    try { const { data } = await api.post(`/projects/${pid}/dpr`, form); toast.success("DPR added"); setOpen(false); setSelected(data); setForm({ ...form, work_done: "", issues: "", notes: "" }); load(); }
    catch { toast.error("Failed"); }
  };
  const remove = async (id) => { if (!window.confirm("Delete this DPR?")) return; await api.delete(`/projects/${pid}/dpr/${id}`); load(); if (selected?.id===id) setSelected(null); };

  const attachPhoto = async (dpr) => {
    const file = fileRef.current?.files?.[0]; if (!file) return;
    setUploading(true);
    try { const fd = new FormData(); fd.append("file", file);
      await api.post(`/projects/${pid}/dpr/${dpr.id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo uploaded"); load();
      const { data } = await api.get(`/projects/${pid}/dpr`); const upd = data.find(r => r.id===dpr.id); if (upd) setSelected(upd);
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const exportPdf = (dpr) => {
    const photos = (dpr.photos||[]).map(p => `<img src="${backendUrl}/api/uploads/projects/${pid}/dpr/${dpr.id}/${p.file_name}" style="max-width:250px;margin:6px;border-radius:6px" />`).join("");
    const body = `
      <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:24px;margin-bottom:6px">
        <div><h1 style="margin:0">Daily Progress Report</h1>
          <div style="color:#73706A;font-size:11px;margin-top:6px">${dpr.date} · Weather: ${dpr.weather}</div></div>
        <div style="text-align:right;color:#73706A;font-size:10px">
          <div>Reported by <b style="color:#1A1918">${dpr.created_by_name || "—"}</b></div>
          <div>${new Date(dpr.created_at).toLocaleString("en-IN")}</div>
        </div>
      </div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:14px;margin-top:14px">
        <div style="padding:14px;background:#F7F5F0;border-radius:8px">
          <div style="font-size:9px;text-transform:uppercase;letter-spacing:1.5px;color:#73706A">Labour on Site</div>
          <div style="font-size:22px;font-weight:700;margin-top:4px">${dpr.labour_count || 0}</div>
        </div>
        <div style="padding:14px;background:#F7F5F0;border-radius:8px">
          <div style="font-size:9px;text-transform:uppercase;letter-spacing:1.5px;color:#73706A">Overall Progress</div>
          <div style="font-size:22px;font-weight:700;margin-top:4px">${dpr.progress || 0}%</div>
        </div>
      </div>
      <h2>Work Done Today</h2><p>${(dpr.work_done||"—").replace(/\n/g,"<br>")}</p>
      ${dpr.issues ? `<h2>Issues & Delays</h2><p>${dpr.issues.replace(/\n/g,"<br>")}</p>` : ""}
      ${(dpr.materials_used||[]).length ? `<h2>Materials Used</h2><table><thead><tr><th>Material</th><th class="right">Qty</th><th>Unit</th></tr></thead><tbody>${dpr.materials_used.map(m => `<tr><td>${m.name}</td><td class="right">${m.qty}</td><td>${m.unit||""}</td></tr>`).join("")}</tbody></table>` : ""}
      ${dpr.notes ? `<h2>Notes</h2><p>${dpr.notes.replace(/\n/g,"<br>")}</p>` : ""}
      ${photos ? `<h2>Site Photos</h2><div style="display:flex;flex-wrap:wrap;margin-top:6px">${photos}</div>` : ""}
    `;
    openBrandedPrintWindow(body, {
      subject: `DPR ${dpr.date}`,
      title: "Daily Progress Report",
      number: dpr.date,
      date: new Date(dpr.created_at).toLocaleDateString("en-IN", { dateStyle: "medium" }),
    });
  };

  const [matName, setMatName] = useState(""); const [matQty, setMatQty] = useState(0); const [matUnit, setMatUnit] = useState("Nos");
  const addMaterial = async (dpr) => {
    if (!matName.trim()) return;
    const materials_used = [...(dpr.materials_used||[]), { name: matName, qty: Number(matQty), unit: matUnit }];
    await api.patch(`/projects/${pid}/dpr/${dpr.id}`, { materials_used });
    setMatName(""); setMatQty(0); load();
    const { data } = await api.get(`/projects/${pid}/dpr`); const upd = data.find(r => r.id===dpr.id); if (upd) setSelected(upd);
  };
  const rmMaterial = async (dpr, idx) => {
    const materials_used = (dpr.materials_used||[]).filter((_,i) => i !== idx);
    await api.patch(`/projects/${pid}/dpr/${dpr.id}`, { materials_used });
    load();
    const { data } = await api.get(`/projects/${pid}/dpr`); const upd = data.find(r => r.id===dpr.id); if (upd) setSelected(upd);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="dpr-tab">
      <div className="lg:col-span-1 rounded-2xl bg-card border border-border/60 overflow-hidden">
        <div className="p-4 border-b border-border/50 flex items-center justify-between">
          <div><h3 className="font-display text-base font-semibold">Daily Reports</h3><p className="text-xs text-muted-foreground">{rows.length} entries</p></div>
          {canEdit && <div className="flex gap-2">
            {canSendDigest && <Button size="sm" variant="outline" onClick={sendDigest} disabled={sendingDigest} data-testid="send-digest-btn" title="Email this week's DPR summary to the project client"><Mail className="w-3.5 h-3.5 mr-1" /> {sendingDigest ? "Sending…" : "Send Weekly Digest"}</Button>}
            <Button size="sm" onClick={() => setOpen(true)} data-testid="new-dpr-btn" className="bg-primary text-primary-foreground"><Plus className="w-3.5 h-3.5 mr-1" /> New</Button>
          </div>}
        </div>
        <div className="divide-y divide-border/50 max-h-[520px] overflow-y-auto">
          {rows.map(r => (
            <button key={r.id} onClick={() => setSelected(r)} data-testid={`dpr-${r.id}`}
              className={`w-full text-left p-4 hover:bg-muted/50 transition-colors ${selected?.id===r.id ? "bg-primary/5 border-l-2 border-primary" : ""}`}>
              <div className="flex items-center justify-between text-sm"><span className="font-medium">{r.date}</span><span className="text-[10px] uppercase text-primary">{r.progress||0}%</span></div>
              <div className="text-xs text-muted-foreground truncate mt-0.5">{r.work_done}</div>
              <div className="text-[10px] text-muted-foreground mt-1 flex items-center gap-3"><span><Users className="w-3 h-3 inline mr-0.5" />{r.labour_count}</span><span>{r.weather}</span>{(r.photos||[]).length>0 && <span><Camera className="w-3 h-3 inline" /> {(r.photos||[]).length}</span>}</div>
            </button>
          ))}
          {!rows.length && <div className="p-8 text-center text-sm text-muted-foreground">No daily reports yet.</div>}
        </div>
      </div>

      {/* Detail pane */}
      <div className="lg:col-span-2">
        {selected ? (
          <div className="rounded-2xl bg-card border border-border/60 overflow-hidden" data-testid="dpr-detail">
            <div className="p-5 border-b border-border/50 flex items-center justify-between flex-wrap gap-2">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Site Report</div>
                <h3 className="font-display text-xl font-semibold">{selected.date}</h3>
                <div className="text-xs text-muted-foreground">Reported by {selected.created_by_name}</div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => exportPdf(selected)} data-testid="dpr-export-btn"><FileDown className="w-3.5 h-3.5 mr-1" /> Export PDF</Button>
                {canEdit && <Button size="sm" variant="outline" onClick={() => remove(selected.id)} data-testid="dpr-delete-btn" className="text-destructive"><Trash2 className="w-3.5 h-3.5" /></Button>}
              </div>
            </div>
            <div className="p-5 grid grid-cols-3 gap-3">
              <div className="rounded-lg bg-muted/30 p-3"><div className="text-[10px] uppercase text-muted-foreground flex items-center gap-1"><Users className="w-3 h-3" /> Labour</div><div className="font-display text-2xl">{selected.labour_count}</div></div>
              <div className="rounded-lg bg-muted/30 p-3"><div className="text-[10px] uppercase text-muted-foreground flex items-center gap-1"><Sun className="w-3 h-3" /> Weather</div><div className="font-display text-xl">{selected.weather}</div></div>
              <div className="rounded-lg bg-muted/30 p-3"><div className="text-[10px] uppercase text-muted-foreground">Progress</div><div className="font-display text-2xl text-primary">{selected.progress}%</div></div>
            </div>
            <div className="p-5 space-y-4">
              <div><div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-1">Work Done</div><p className="text-sm">{selected.work_done}</p></div>
              {selected.issues && <div><div className="text-[10px] uppercase text-muted-foreground mb-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3 text-rose-500" /> Issues & Delays</div><p className="text-sm">{selected.issues}</p></div>}
              {selected.notes && <div><div className="text-[10px] uppercase text-muted-foreground mb-1">Notes</div><p className="text-sm">{selected.notes}</p></div>}

              <div>
                <div className="text-[10px] uppercase text-muted-foreground mb-2 flex items-center gap-1"><Wrench className="w-3 h-3" /> Materials Used</div>
                <div className="space-y-1 mb-2">
                  {(selected.materials_used||[]).map((m,i) => (
                    <div key={i} className="flex items-center justify-between bg-muted/40 rounded px-3 py-1.5 text-xs" data-testid={`dpr-material-${i}`}>
                      <span>{m.name}</span><span className="text-muted-foreground">{m.qty} {m.unit||""}</span>
                      {canEdit && <button onClick={() => rmMaterial(selected, i)} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-3 h-3" /></button>}
                    </div>
                  ))}
                </div>
                {canEdit && (
                  <div className="grid grid-cols-4 gap-2">
                    <Input placeholder="Material" value={matName} onChange={e=>setMatName(e.target.value)} className="col-span-2 h-8 text-xs" data-testid="dpr-mat-name" />
                    <Input placeholder="Qty" type="number" value={matQty} onChange={e=>setMatQty(e.target.value)} className="h-8 text-xs" data-testid="dpr-mat-qty" />
                    <div className="flex gap-1"><Input placeholder="Unit" value={matUnit} onChange={e=>setMatUnit(e.target.value)} className="h-8 text-xs flex-1" /><Button size="sm" onClick={() => addMaterial(selected)} data-testid="dpr-mat-add"><Plus className="w-3.5 h-3.5" /></Button></div>
                  </div>
                )}
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10px] uppercase text-muted-foreground flex items-center gap-1"><Camera className="w-3 h-3" /> Site Photos ({(selected.photos||[]).length})</div>
                  {canEdit && <label className="inline-block">
                    <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={() => attachPhoto(selected)} data-testid="dpr-photo-input" />
                    <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="dpr-photo-btn"><Camera className="w-3.5 h-3.5 mr-1" /> {uploading ? "Uploading…" : "Add photo"}</Button>
                  </label>}
                </div>
                <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
                  {(selected.photos||[]).map(p => (
                    <a key={p.id} href={`${backendUrl}/api/uploads/projects/${pid}/dpr/${selected.id}/${p.file_name}`} target="_blank" rel="noreferrer" className="block relative aspect-square rounded-lg overflow-hidden bg-muted">
                      <img src={`${backendUrl}/api/uploads/projects/${pid}/dpr/${selected.id}/${p.file_name}`} alt={p.original_name} className="object-cover w-full h-full" />
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ) : <div className="rounded-2xl border border-dashed border-border/60 p-10 text-center text-sm text-muted-foreground">Select a report to view details, or create a new one.</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>New Daily Progress Report</DialogTitle><DialogDescription>Log today's site progress, labour, materials and issues.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Input type="date" value={form.date} onChange={e=>setForm({...form,date:e.target.value})} data-testid="dpr-date-input" />
            <Select value={form.weather} onValueChange={v=>setForm({...form,weather:v})}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{WEATHER.map(w=><SelectItem key={w} value={w}>{w}</SelectItem>)}</SelectContent>
            </Select>
            <Input type="number" placeholder="Labour count" value={form.labour_count} onChange={e=>setForm({...form,labour_count:Number(e.target.value)})} data-testid="dpr-labour-input" />
            <Input type="number" min="0" max="100" placeholder="Progress %" value={form.progress} onChange={e=>setForm({...form,progress:Number(e.target.value)})} data-testid="dpr-progress-input" />
            <Textarea className="col-span-2" rows={4} placeholder="Work done today…" value={form.work_done} onChange={e=>setForm({...form,work_done:e.target.value})} data-testid="dpr-work-done-input" />
            <Textarea className="col-span-2" rows={2} placeholder="Issues & delays (optional)" value={form.issues} onChange={e=>setForm({...form,issues:e.target.value})} data-testid="dpr-issues-input" />
            <Textarea className="col-span-2" rows={2} placeholder="Notes" value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})} />
          </div>
          <Button onClick={create} className="bg-primary text-primary-foreground" data-testid="save-dpr-btn">Save Report</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
